import { apiFetch } from './http';
import { uploadBlockFile, type UploadPayload } from './uploads';
import { toBlock, type Block, type BlockRow, type CreateBlockInput, type UpdateBlockInput } from './types';
import { invalidateAllCaches } from './server';
import { clearBlockPreviewCache } from './channels';

function toPayload(file?: UploadPayload | Blob | null): UploadPayload | Blob | null {
  return file || null;
}

/** Owner's blocks for a channel (requires auth), ordered by position. */
export async function fetchBlocksByChannel(channelId: string): Promise<Block[]> {
  const rows = await apiFetch<BlockRow[]>(`/api/channels/${encodeURIComponent(channelId)}/blocks`);
  return rows.map(toBlock);
}

/**
 * Owner-scoped full-text search across all of the owner's blocks (requires
 * auth), ranked by relevance. Results carry `channelSlug` for navigation.
 * Queries under 2 characters return [] server-side.
 */
export async function searchBlocks(query: string): Promise<Block[]> {
  const rows = await apiFetch<BlockRow[]>(`/api/blocks/search?q=${encodeURIComponent(query)}`);
  return rows.map(toBlock);
}

export async function createBlock(
  input: CreateBlockInput & {
    imageFile?: UploadPayload | Blob | null;
    pdfFile?: UploadPayload | Blob | null;
  },
): Promise<Block> {
  let imagePath: string | null = null;
  let pdfPath: string | null = null;

  const imagePayload = toPayload(input.imageFile);
  const pdfPayload = toPayload(input.pdfFile);

  if (imagePayload) {
    imagePath = await uploadBlockFile(imagePayload);
  }

  if (input.type === 'pdf' && pdfPayload) {
    pdfPath = await uploadBlockFile(pdfPayload);
  }

  const row = await apiFetch<BlockRow>('/api/blocks', {
    method: 'POST',
    body: JSON.stringify({
      channelId: input.channelId,
      type: input.type,
      title: input.title,
      content: input.content || null,
      sourceUrl: input.sourceUrl || null,
      sourceAuthor: input.sourceAuthor || null,
      imagePath,
      pdfPath,
      previewImagePath: input.previewImagePath || null,
    }),
  });

  invalidateAllCaches();
  clearBlockPreviewCache();
  return toBlock(row);
}

export async function uploadBlockImage(file: UploadPayload | Blob): Promise<string> {
  return uploadBlockFile(file);
}

function toBlockUpdateBody(updates: UpdateBlockInput): Record<string, unknown> {
  return {
    ...(updates.title !== undefined ? { title: updates.title } : {}),
    ...(updates.content !== undefined ? { content: updates.content } : {}),
    ...(updates.sourceUrl !== undefined ? { sourceUrl: updates.sourceUrl } : {}),
    ...(updates.sourceAuthor !== undefined ? { sourceAuthor: updates.sourceAuthor } : {}),
    ...(updates.imagePath !== undefined ? { imagePath: updates.imagePath } : {}),
    ...(updates.pdfPath !== undefined ? { pdfPath: updates.pdfPath } : {}),
    ...(updates.source_url !== undefined ? { sourceUrl: updates.source_url } : {}),
    ...(updates.source_author !== undefined ? { sourceAuthor: updates.source_author } : {}),
    ...(updates.image_path !== undefined ? { imagePath: updates.image_path } : {}),
    ...(updates.pdf_path !== undefined ? { pdfPath: updates.pdf_path } : {}),
  };
}

export async function updateBlock(id: string, updates: UpdateBlockInput): Promise<Block> {
  const row = await apiFetch<BlockRow>(`/api/blocks/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(toBlockUpdateBody(updates)),
  });
  invalidateAllCaches();
  clearBlockPreviewCache();
  return toBlock(row);
}

export async function deleteBlock(id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/api/blocks/${encodeURIComponent(id)}`, { method: 'DELETE' });
  invalidateAllCaches();
  clearBlockPreviewCache();
}
